import React, { useEffect } from 'react';
import {
    Box,
    TextField,
    Button,
    Typography,
    Paper,
} from '@mui/material';
import { closeButton, closeButtonInner, form, formInner, overlay } from "./playerFormStyles";
import CloseIcon from '@mui/icons-material/Close';
import { Controller, useForm } from "react-hook-form";
import { useAppDispatch } from "../../../app/hooks";
import { createPlayer, ICreatePlayerPayload, IPlayer, IUpdatePlayerPayload, updatePlayer } from "../playersSlice";
import { isValidData } from "../../../helpers/isValidData";
import { toast } from "react-toastify";
import { DatePicker, LocalizationProvider } from "@mui/x-date-pickers";
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFnsV3';

interface IPlayerFormProps {
    editingPlayer: IPlayer | null;
    onCloseForm: () => void;
}

interface IPlayerFormData {
    name: string;
    surname: string;
    nickname: string;
    displayname: string;
    dateOfBirth: Date | null;
    facebook: string;
    linkedIn: string;
    comments: string;
    hometown: string;
    country: string;
    telephone: string;
    email: string;
    webSite: string;
}

const PlayerForm: React.FC<IPlayerFormProps> = ({ editingPlayer, onCloseForm }) => {
    const dispatch = useAppDispatch();

    const { handleSubmit, control, reset, formState: { errors } } = useForm<IPlayerFormData>({
        defaultValues: {
            name: "",
            surname: "",
            nickname: "",
            displayname: "",
            dateOfBirth: null,
            facebook: "",
            linkedIn: "",
            comments: "",
            hometown: "",
            country: "",
            telephone: "",
            email: "",
            webSite: ""
        }
    });

    useEffect(() => {
        if (editingPlayer) {
            reset({
                name: editingPlayer.name || "",
                surname: editingPlayer.surname || "",
                nickname: editingPlayer.nickname || "",
                displayname: editingPlayer.displayname || "",
                dateOfBirth: editingPlayer.dateOfBirth
                    ? new Date(editingPlayer.dateOfBirth)
                    : null,
                facebook: editingPlayer.socialMedia?.facebook || "",
                linkedIn: editingPlayer.socialMedia?.linkedIn || "",
                comments: editingPlayer.socialMedia?.comments || "",
                hometown: editingPlayer.contact?.hometown || "",
                country: editingPlayer.contact?.country || "",
                telephone: editingPlayer.contact?.telephon || "",
                email: editingPlayer.contact?.email || "",
                webSite: editingPlayer.contact?.webSite || ""
            });
        }
    }, [editingPlayer, reset]);

    const onSubmit = (data: IPlayerFormData) => {
        if (!isValidData(data)) {
            toast.warning("SDC: Fill the form");
            return;
        }

        if (!editingPlayer) {
            const playerInfo = {
                name: data.name,
                surname: data.surname,
                nickname: data.nickname,
                displayname: data.displayname,
                dateOfBirth: data.dateOfBirth,
                socialMedia: {
                    facebook: data.facebook,
                    linkedIn: data.linkedIn,
                    comments: data.comments
                },
                contact: {
                    hometown: data.hometown,
                    country: data.country,
                    telephon: data.telephone,
                    email: data.email,
                    webSite: data.webSite
                }
            };

            const body: ICreatePlayerPayload = {
                payload: playerInfo,
                closeForm: onCloseForm
            };

            dispatch(createPlayer(body));
        } else {
            const reqBody: IUpdatePlayerPayload = {
                payload: {
                    id: editingPlayer.id,
                    socialMedia: {
                        facebook: data.facebook,
                        linkedIn: data.linkedIn,
                        comments: data.comments
                    },
                    contact: {
                        hometown: data.hometown,
                        country: data.country,
                        telephon: data.telephone,
                        email: data.email,
                        webSite: data.webSite
                    }
                },
                closeForm: onCloseForm
            };

            dispatch(updatePlayer(reqBody));
        }
    };

    const fields: {
        name: keyof IPlayerFormData;
        label: string;
        multiline?: boolean;
        isDate?: boolean;
    }[] = [
        { name: "name", label: "Name" },
        { name: "surname", label: "Surname" },
        { name: "dateOfBirth", label: "Date of Birth", isDate: true },
        { name: "nickname", label: "Nickname" },
        { name: "displayname", label: "Display name" },
        { name: "facebook", label: "Facebook" },
        { name: "linkedIn", label: "LinkedIn" },
        { name: "comments", label: "Comments", multiline: true },
        { name: "country", label: "Country" },
        { name: "hometown", label: "Home town" },
        { name: "telephone", label: "Telephone" },
        { name: "email", label: "Email" },
        { name: "webSite", label: "Website" }
    ];

    return (
        <Box sx={overlay}>
            <Box>
                <Paper sx={formInner}>
                    <Box sx={closeButtonInner}>
                        <Button sx={closeButton} onClick={onCloseForm}>
                            <CloseIcon sx={{ width: 20 }} />
                        </Button>
                    </Box>
                    <Typography variant="h5" align="center">
                        {editingPlayer ? `Edit Player` : `Create player`}
                    </Typography>
                    <Box component="form" onSubmit={handleSubmit(onSubmit)}>
                        <Box sx={form}>
                            {fields.map(({ name, label, multiline, isDate }) => (
                                <Controller
                                    key={name}
                                    name={name}
                                    control={control}
                                    rules={
                                        isDate
                                            ? {
                                                required: "Date of birth is required",
                                                validate: (v) =>
                                                    v instanceof Date && !isNaN(v.getTime()) || "Invalid date"
                                            }
                                            : {
                                                minLength: 2,
                                                maxLength: 150,
                                                pattern: {
                                                    value: /[^\s]/,
                                                    message: "Field can not contain only spaces"
                                                }
                                            }
                                    }
                                    render={({ field }) =>
                                        isDate ? (
                                            <LocalizationProvider dateAdapter={AdapterDateFns}>
                                                <DatePicker
                                                    label={label}
                                                    value={field.value instanceof Date || field.value === null
                                                        ? field.value
                                                        : new Date(field.value)}
                                                    onChange={(date) => field.onChange(date)}
                                                    slotProps={{
                                                        textField: {
                                                            variant: "outlined",
                                                            fullWidth: true,
                                                            error: !!errors[name],
                                                            helperText: errors[name]?.message
                                                        }
                                                    }}
                                                />
                                            </LocalizationProvider>
                                        ) : (
                                            <TextField
                                                {...field}
                                                label={label}
                                                multiline={multiline}
                                                rows={multiline ? 4 : undefined}
                                                error={!!errors[name]}
                                                helperText={errors[name]?.message}
                                            />
                                        )
                                    }
                                />
                            ))}

                            <Button variant="contained" type="submit">
                                Submit
                            </Button>
                        </Box>
                    </Box>
                </Paper>
            </Box>
        </Box>
    );
};

export default PlayerForm;
